import { cn } from '@repo/tailwind-config/tailwind.functions'
import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { SVGFC } from '@repo/toolkit/types'
import { PropsWithChildren } from 'react'

import WavyCircleIcon from '../../../icons/wavy-circle'
import XIcon from '../../../icons/x'
import Typography from '../../atoms/typography'

export interface ModalHeaderProps {
  title?: string
  titleAlignment?: 'center' | 'left'
  icon?: SVGFC
  isIconCentered?: boolean
  isDestructive?: boolean
  hasBorderBottom?: boolean
  onClose: () => void
}

const ModalHeader: React.FC<PropsWithChildren<ModalHeaderProps>> = ({
  title,
  titleAlignment = 'center',
  icon,
  isIconCentered,
  isDestructive,
  children,
  hasBorderBottom = true,
  onClose
}) => {
  return (
    <div
      className={cn('flex items-center justify-between py-6 px-4 relative', {
        'items-start justify-center p-10': isIconCentered,
        'border-b border-b-white-active': hasBorderBottom,
        'pb-4': !hasBorderBottom
      })}
    >
      <div
        className={cn('flex flex-row items-center gap-2', {
          'flex-col justify-center items-center gap-10': isIconCentered
        })}
      >
        {icon && (
          <div
            className={cn('flex items-center justify-center p-2 rounded', {
              'p-4 rounded-3xl': isIconCentered,
              'bg-secondary-light': !isDestructive,
              'bg-red-light': isDestructive
            })}
          >
            {isIconCentered ? (
              <div className="relative flex items-center justify-center">
                <WavyCircleIcon
                  color={
                    isDestructive ? tailwindColors.red.DEFAULT : tailwindColors.secondary.DEFAULT
                  }
                />
                <div className="absolute flex items-center justify-center">
                  {icon({
                    color: tailwindColors.white.DEFAULT,
                    height: '32px',
                    width: '32px'
                  })}
                </div>
              </div>
            ) : (
              icon({
                color: tailwindColors.secondary.DEFAULT,
                height: '18px',
                width: '18px'
              })
            )}
          </div>
        )}

        {title && (
          <div className={cn('flex flex-row items-center gap-2')}>
            <Typography
              variant="body-medium"
              className={cn({
                'text-center': titleAlignment === 'center',
                'text-left': titleAlignment === 'left'
              })}
              color={tailwindColors.grey.DEFAULT}
            >
              {title}
            </Typography>

            {children}
          </div>
        )}
      </div>

      <button
        aria-label="Close"
        className={cn(
          'p-1 hover:bg-grey-light-hover/50 rounded-full transition-colors duration-200 ease-in-out',
          { 'absolute top-4 right-4': isIconCentered }
        )}
        onClick={onClose}
      >
        <XIcon color={tailwindColors.grey.light.active} height="12px" width="12px" />
      </button>
    </div>
  )
}

export default ModalHeader
